import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ApiService } from 'services/api.service';
import { DESKTOP_LG_END, DESKTOP_SM_END } from 'util/breakpoints.utility';
import { environment } from 'env/environment';
import { Observable, forkJoin } from 'rxjs';
import { ToastService } from 'services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
  standalone: true
})
export abstract class BaseFormComponent implements OnInit {
  @Output() editModeChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  isEditMode: boolean = false;
  submitAttempted: boolean = false;
  form: FormGroup;
  isOpenConfirmBack: boolean = false;
  fileActions: Observable<any>[] = [];
  uploadComponentsReceived: number = 0;
  fileUploadComponentCount: number = 0;

  get isDesktopSm(): boolean {
    return window.innerWidth <= DESKTOP_SM_END;
  }

  get isDesktopMdPlus(): boolean {
    return window.innerWidth >= DESKTOP_SM_END;
  }

  get isDesktopLgPlus(): boolean {
    return window.innerWidth >= DESKTOP_LG_END;
  }

  get isMobile(): boolean {
    return environment.isMobile;
  }

  constructor(
    protected location: Location,
    protected router: Router,
    protected formBuilder: FormBuilder,
    protected apiService: ApiService,
    protected toastService: ToastService,
    protected translateService: TranslateService
  ) {
    this.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.fillForm();
  }

  abstract fillForm(): void;

  setIsEditMode(isEditMode: boolean) {
    this.isEditMode = isEditMode;
    this.editModeChanged.emit(isEditMode);
  }

  submit(): void {
    this.submitAttempted = true;
  }

  shouldRenderError(key: string, formGroup?: FormGroup<any>): boolean {
    const control = !formGroup ? this.form.get(key) : formGroup.get(key);

    if (this.submitAttempted && control?.invalid) {
      return true;
    } else if (control) {
      return control.invalid && control.touched && !!control?.errors;
    }

    return false;
  }

  getErrorMessage(controlName: string, formGroup?: FormGroup<any>): string | null {
    if (this.shouldRenderError(controlName, formGroup)) {
      const control = !formGroup ? this.form.get(controlName) : formGroup.get(controlName);

      if (control?.errors) {
        if (control.errors['required']) {
          if (controlName === 'visitDate') {
            return 'components.form.errorRequiredVisitDate';
          } else if (controlName === 'visitTimeFrom') {
            return 'components.form.errorRequiredVisitTimeFrom';
          } else {
            return 'components.form.errorRequired';
          }
        } else if (control.errors['minlength']) {
          return this.translateService.instant('components.form.minLength', {
            minLength: control.errors['minlength']?.requiredLength
          });
        } else if (control.errors['maxlength']) {
          return this.translateService.instant('components.form.maxLength', {
            maxLength: control.errors['maxlength'].requiredLength
          });
        } else if (control.errors['invalidPhone']) {
          return this.translateService.instant('components.form.invalidPhone');
        } else if (control.errors['invalidEmail']) {
          return this.translateService.instant('components.form.invalidEmail');
        }
      }
    }

    return null;
  }

  onFieldBlur(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      control.markAsTouched();
    }
  }

  back(confirmed: boolean = false): void {
    if (!this.form.dirty || confirmed) {
      this.location.back();
    } else {
      this.isOpenConfirmBack = true;
    }
  }

  navigateTo(url: string): void {
    this.router.navigate([url]);
  }

  handleFileRequests(fileActions: Observable<any>[]): void {
    this.fileActions = [...this.fileActions, ...fileActions];

    if (!this.fileUploadComponentCount) {
      return;
    } else {
      this.uploadComponentsReceived++;
    }

    if (this.uploadComponentsReceived === this.fileUploadComponentCount) {
      if (this.fileActions.length === 0) {
        this.onSubmitSuccess();
      } else {
        // Combine all observables
        forkJoin(this.fileActions).subscribe(
          () => {
            this.onSubmitSuccess();
          },
          (error: any) => {
            console.error('Error in combined file operations:', error);
            this.toastService.showRequestError();
          }
        );
      }
    }
  }

  protected onSubmitSuccess(): void {
    // Override this method in child components
  }

  onDateSelected(date: string, key: string): void {
    this.form.get(key)?.markAsDirty();
    this.form.get(key)?.markAsTouched();
    this.form.get(key)?.setValue(date);
  }

  markAsTouched(key: string): void {
    this.form.get(key)?.markAsTouched();
  }

  onTimeChange(time: string | null, key: string): void {
    this.form.get(key)?.markAsDirty();
    this.form.get(key)?.markAsTouched();
    this.form.get(key)?.setValue(time);
  }
}
